import classNames from 'classnames';
import type { FC, LabelHTMLAttributes, ReactNode } from 'react';
import { FormField } from '../FormField/FormField';
import type {
  FormFieldBaseProps,
  FormFieldToggleBaseProps,
} from '../FormField/FormField.types';
import { FormFieldHelperText } from '../FormFieldHelperText/FormFieldHelperText';
import { Label } from '../Label/Label';
import { Switch } from '../Switch/Switch';
import type { SwitchProps } from '../Switch/Switch.types';
import type { InteractiveAtomSize } from '../utils/InteractiveAtomProps';

export type SwitchFieldProps = SwitchProps & FormFieldToggleBaseProps;

export const SwitchField = (props: SwitchFieldProps) => {
  const {
    className,
    description,
    disabled,
    helperText,
    label,
    labelTooltip,
    error,
    hasError,
    innerRef,
    ...restProps
  } = props;
  return (
    <FormField
      {...restProps}
      className={className}
      error={error}
      disabled={disabled}
    >
      <SwitchLabel
        {...restProps}
        control={
          <Switch
            {...restProps}
            hasError={hasError || error !== undefined}
            disabled={disabled}
            innerRef={innerRef}
          />
        }
        description={description}
        label={label}
        labelTooltip={labelTooltip}
        disabled={disabled}
        helperText={helperText}
      />
    </FormField>
  );
};

type SwitchLabelSize = InteractiveAtomSize;

type SwitchLabelProps = Omit<
  FormFieldBaseProps,
  'error' | 'primaryHelperText' | 'secondaryHelperText'
> & {
  /**
   * The control element (e.g., checkbox, radio button) to be rendered.
   *
   * To make labels work properly, the control must contain an element
   * triggered when wrapped inside a <label> tag by clicking on the label
   * (input type=checkbox|radio, button, etc.) or `htmlFor` should be
   * delivered via `labelProps`.
   */
  control: ReactNode;
  helperText?: ReactNode;
  labelProps?: LabelHTMLAttributes<HTMLLabelElement>;
};

const sizeClassName: { [Key in SwitchLabelSize]: string } = {
  small: 'text-xs',
  default: 'text-sm',
  large: 'text-base',
};

const gapClassName: { [Key in SwitchLabelSize]: string } = {
  small: 'gap-3xs',
  default: 'gap-xs',
  large: 'gap-xs',
};

/**
 * Wraps switch control with a label element, displaying optional label, description and helper text.
 * Clicking on the label toggles the control state (e.g., checked/unchecked).
 * Note: Checkbox and radio components uses are handled differently via SelectionControlLabel.
 */
const SwitchLabel: FC<SwitchLabelProps> = ({
  className,
  control,
  label,
  labelTooltip,
  description,
  helperText,
  size = 'default',
  labelProps = undefined,
  disabled,
  ...restProps
}) => (
  // biome-ignore lint/a11y/noLabelWithoutControl: There is always a button inside the switch. Documented in the control prop.
  <label
    className={classNames(
      'flex items-start',
      'mb-0', // this is a workaround to locally reset the default bottom margin of the label tag from bootstrap.min.css
      gapClassName[size],
      className,
    )}
    {...labelProps}
    {...restProps}
  >
    {control}
    <div className='flex flex-col gap-3xs'>
      {label && (
        <Label tooltipContent={labelTooltip} size={size} renderAs='div'>
          {label}
        </Label>
      )}
      {description && (
        <span
          className={classNames(
            'text-default font-normal',
            sizeClassName[size],
          )}
        >
          {description}
        </span>
      )}
      {helperText && (
        <FormFieldHelperText size={size} disabled={disabled} variant='plain'>
          {helperText}
        </FormFieldHelperText>
      )}
    </div>
  </label>
);
